import React, { useState } from 'react';
import { motion } from 'motion/react';
import { DroneMission } from '../types';
import { Icons } from '../constants';

interface Props {
  mission?: Partial<DroneMission>;
  onInitiate?: () => void;
}

const UAVMissionModule: React.FC<Props> = ({ mission, onInitiate }) => {
  const [checks, setChecks] = useState({
    airspace: false,
    battery: false,
    weather: false,
    signal: false
  });
  const [missionActive, setMissionActive] = useState(false);

  const allChecked = Object.values(checks).every(Boolean);

  const handleInitiate = () => {
    if (allChecked) {
      setMissionActive(true);
      if (onInitiate) onInitiate();
    }
  };

  return (
    <div className="bg-stone-900 text-white rounded-3xl p-8 overflow-hidden relative print:hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-palm/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      
      <div className="relative z-10">
        <div className="flex items-center space-x-3 mb-6">
          <Icons.Drone className="w-8 h-8 text-palm" />
          <h3 className="text-2xl font-black uppercase tracking-widest">UAV Mission Protocol</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Instructions & Compliance */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest">By The Numbers</h4>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 rounded-full bg-palm text-white flex items-center justify-center font-black flex-shrink-0">1</div>
                <div>
                  <h5 className="font-bold text-lg">Site Calibration</h5>
                  <p className="text-sm text-stone-400">Establish home point and calibrate compass away from magnetic interference.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 rounded-full bg-palm text-white flex items-center justify-center font-black flex-shrink-0">2</div>
                <div>
                  <h5 className="font-bold text-lg">Exclusion Zone</h5>
                  <p className="text-sm text-stone-400">Verify 30m safety perimeter is clear of non-participants.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 rounded-full bg-palm text-white flex items-center justify-center font-black flex-shrink-0">3</div>
                <div>
                  <h5 className="font-bold text-lg">Flight Pattern</h5>
                  <p className="text-sm text-stone-400">Ascend to target altitude (40m AGL) and commence automated grid pattern.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 rounded-full bg-palm text-white flex items-center justify-center font-black flex-shrink-0">4</div>
                <div>
                  <h5 className="font-bold text-lg">Data Capture</h5>
                  <p className="text-sm text-stone-400">Ensure sensor payload is actively recording with adequate overlap.</p>
                </div>
              </div>
            </div>

            <div className="bg-stone-800 p-6 rounded-2xl border border-stone-700">
              <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-4">Compliance Dashboard</h4>
              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => setChecks(p => ({ ...p, airspace: !p.airspace }))}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${checks.airspace ? 'bg-gecko/20 border-gecko text-gecko' : 'border-stone-600 text-stone-400 hover:border-stone-500'}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-sm">CAA/FAA Airspace</span>
                    {checks.airspace && <Icons.Check className="w-4 h-4" />}
                  </div>
                </button>
                <button 
                  onClick={() => setChecks(p => ({ ...p, battery: !p.battery }))}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${checks.battery ? 'bg-gecko/20 border-gecko text-gecko' : 'border-stone-600 text-stone-400 hover:border-stone-500'}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-sm">Battery Health</span>
                    {checks.battery && <Icons.Check className="w-4 h-4" />}
                  </div>
                </button>
                <button 
                  onClick={() => setChecks(p => ({ ...p, weather: !p.weather }))}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${checks.weather ? 'bg-gecko/20 border-gecko text-gecko' : 'border-stone-600 text-stone-400 hover:border-stone-500'}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-sm">Weather Limits</span>
                    {checks.weather && <Icons.Check className="w-4 h-4" />}
                  </div>
                </button>
                <button 
                  onClick={() => setChecks(p => ({ ...p, signal: !p.signal }))}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${checks.signal ? 'bg-gecko/20 border-gecko text-gecko' : 'border-stone-600 text-stone-400 hover:border-stone-500'}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-sm">Signal Integrity</span>
                    {checks.signal && <Icons.Check className="w-4 h-4" />}
                  </div>
                </button>
              </div>

              <button 
                onClick={handleInitiate}
                disabled={!allChecked || missionActive}
                className={`w-full mt-6 py-4 rounded-xl font-black uppercase tracking-widest transition-all ${
                  missionActive ? 'bg-blue-500 text-white animate-pulse' :
                  allChecked ? 'bg-palm text-white hover:bg-gecko shadow-lg shadow-palm/20' : 'bg-stone-700 text-stone-500 cursor-not-allowed'
                }`}
              >
                {missionActive ? 'Mission Active' : 'Initiate UAV Mission'}
              </button>
            </div>
          </div>

          {/* Right: 3D Infographic */}
          <div className="bg-stone-800 rounded-2xl border border-stone-700 p-6 flex flex-col items-center justify-center relative overflow-hidden min-h-[400px]">
            <h4 className="absolute top-6 left-6 text-sm font-bold text-stone-400 uppercase tracking-widest z-20">Flight Path Visualization</h4>
            
            {/* 3D Scene Mockup */}
            <div className="relative w-full h-full flex items-center justify-center perspective-1000">
              {/* Ground Plane */}
              <motion.div 
                className="absolute w-64 h-64 border-2 border-stone-600 rounded-full"
                style={{ rotateX: 60 }}
                animate={{ rotateZ: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                {/* Exclusion Zone Indicator */}
                <div className="absolute inset-4 border border-red-500/30 rounded-full bg-red-500/5"></div>
                {/* Target Area */}
                <div className="absolute inset-20 border border-palm/50 rounded-full bg-palm/10 flex items-center justify-center">
                  <Icons.Tree className="w-8 h-8 text-palm -rotate-x-60" />
                </div>
              </motion.div>

              {/* Drone Altitude Line */}
              <div className="absolute w-px h-32 bg-gradient-to-t from-palm to-transparent bottom-1/2 translate-y-8"></div>

              {/* Drone */}
              <motion.div 
                className="absolute text-white"
                animate={missionActive ? { 
                  y: [-20, -40, -20],
                  x: [-30, 30, -30],
                  rotateZ: [-5, 5, -5]
                } : {
                  y: [0, -10, 0]
                }}
                transition={{ duration: missionActive ? 4 : 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Icons.Drone className="w-12 h-12 drop-shadow-[0_0_15px_rgba(55,110,32,0.5)]" />
                {missionActive && (
                  <motion.div 
                    className="absolute top-full left-1/2 -translate-x-1/2 w-16 h-32 bg-gradient-to-b from-white/20 to-transparent"
                    style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}
                    animate={{ opacity: [0.2, 0.5, 0.2] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  ></motion.div>
                )}
              </motion.div>

              {/* Altitude Indicator */}
              <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col items-center space-y-2">
                <span className="text-xs font-mono text-stone-500">60m</span>
                <div className="w-1 h-32 bg-stone-700 rounded-full relative">
                  <motion.div 
                    className="absolute bottom-0 w-full bg-palm rounded-full"
                    animate={{ height: missionActive ? '66%' : '10%' }}
                    transition={{ duration: 2, ease: "easeInOut" }}
                  ></motion.div>
                </div>
                <span className="text-xs font-mono text-stone-500">0m</span>
                <span className="text-xs font-bold text-palm mt-2">{missionActive ? '40m AGL' : 'GND'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UAVMissionModule;
